import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppTranslateLanguageService } from '@core/services/app-translate-language.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of, shareReplay, switchMap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { transformFuelFilterRequestBody, type FuelFilterRequestBody } from './fuel-filter-body.transform';

interface FuelPageable {
  offset?: number;
  pageSize?: number;
  sort?: Array<{ property?: string; ascending?: boolean }>;
}

interface FuelResponseBody {
  status?: number;
  pageable?: FuelPageable;
  total_element?: number;
  list_data?: Record<string, unknown>[];
  data?: unknown;
  branchList?: Record<string, unknown>[];
}

interface BranchRow {
  id?: number | string;
  number?: string;
  code?: string;
  description?: string;
}

const branchCache = new Map<string | number, Observable<BranchRow[]>>();

function getAcceptLanguage(translate: TranslateService, langService: AppTranslateLanguageService): string {
  return translate.getCurrentLang() || langService.getStoredLanguage();
}

function getCompanyId(): number | string | null {
  try {
    const raw = sessionStorage.getItem('userEnv');
    if (!raw) return null;
    const env = JSON.parse(raw) as { company?: { id?: number | string } };
    return env?.company?.id ?? null;
  } catch {
    return null;
  }
}

function fetchBranches(http: HttpClient, companyId: string | number): Observable<BranchRow[]> {
  if (branchCache.has(companyId)) {
    return branchCache.get(companyId)!;
  }

  const baseUrl = environment.apiUrl.endsWith('/') ? environment.apiUrl : environment.apiUrl + '/';
  const request$ = http
    .post<{ data?: BranchRow[] }>(baseUrl + 'branch/filter', {
      filter: {
        company: { id: companyId },
        status: 'ACTIVE',
      },
      query: {
        paging: { limit: 5000, offset: 0, fetch: 'EAGER' },
      },
    })
    .pipe(
      map((res) => res?.data ?? []),
      catchError(() => of([] as BranchRow[])),
      shareReplay({ bufferSize: 1, refCount: false })
    );

  branchCache.set(companyId, request$);
  return request$;
}

function transformResponse(body: FuelResponseBody, branches: BranchRow[]): Record<string, unknown> {
  const rows: Record<string, unknown>[] =
    (body.data as Record<string, unknown>[]) ??
    body.list_data?.map((item) => {
      const mapped = { ...item };
      if (item['branch_id'] && branches.length) {
        const branch = branches.find((b) => b.id === item['branch_id']);
        if (branch) {
          mapped['branch_code'] = branch.number || branch.code;
          mapped['branch_description'] = branch.description;
          mapped['branch_number_description'] = (branch.number ?? '') + ' - ' + (branch.description ?? '');
        }
      }
      return mapped;
    }) ??
    [];

  return {
    outcome_type: body.status === 200 ? 'success' : 'error',
    status: 0,
    query: {
      sort:
        body.pageable?.sort?.map((s) => ({
          name: s.property,
          asc: s.ascending,
        })) ?? [],
      paging: {
        offset: body.pageable?.offset ?? 0,
        limit: body.pageable?.pageSize ?? 10,
        total: body.total_element ?? 0,
        fetch: 'EAGER',
      },
    },
    data: rows,
  };
}

function isJretailFilterBody(body: Record<string, unknown>): boolean {
  const hasLegacyEnv =
    'company' in body || 'working_period' in body || 'company_id' in body || 'working_period_id' in body;
  return hasLegacyEnv && !('search_requests' in body);
}

/** Convert kl-grid / fuel-env-filter body to fuel-management-service filter shape. */
function transformFuelFilterRequest(body: Record<string, unknown>): Record<string, unknown> {
  const searchRequests: Array<Record<string, unknown>> = [];

  const company = body['company'] as { id?: number | string } | undefined;
  const companyId = company?.id ?? body['company_id'];
  if (companyId != null) {
    searchRequests.push({
      column: 'companyId',
      operation: 'CONTAINS',
      value: companyId,
      join_table: 'string',
    });
  }

  const pageSize =
    (body['_page_size'] as number) ||
    ((body['page'] as Record<string, unknown> | undefined)?.['page_size'] as number) ||
    10;
  const offset = (body['_offset'] as number) ?? 0;
  const pageNo = Math.floor(offset / pageSize);

  const envelope: Record<string, unknown> = {
    search_requests: searchRequests,
    global_operator: 'OR',
    page: {
      page_no: pageNo,
      page_size: pageSize,
      sort: 'ASC',
      sort_by_column: 'id',
    },
  };

  if (body['_parameters'] !== undefined) {
    envelope['_parameters'] = body['_parameters'];
  }
  if (body['_offset'] !== undefined) {
    envelope['_offset'] = body['_offset'];
  }
  if (body['_page_size'] !== undefined) {
    envelope['_page_size'] = body['_page_size'];
  }

  for (const [key, value] of Object.entries(body)) {
    if (
      key === 'company' ||
      key === 'working_period' ||
      key === 'company_id' ||
      key === 'working_period_id' ||
      key === '_offset' ||
      key === '_page_size' ||
      key === '_parameters' ||
      key === 'page' ||
      key === 'search_requests' ||
      key === 'global_operator' ||
      key === 'compress'
    ) {
      continue;
    }
    const valueType = typeof value;
    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
      if (value !== '') {
        envelope[key] = value;
      }
    }
  }

  return transformFuelFilterRequestBody(envelope as FuelFilterRequestBody);
}

function syncFuelPageNo(body: Record<string, unknown>): Record<string, unknown> {
  const page = body['page'] as Record<string, unknown> | undefined;
  if (!page || !('page_no' in page)) {
    return body;
  }
  const pageSize = (page['page_size'] as number) || 10;
  const { _offset, _page_size, ...rest } = body;
  let offset = 0;
  if (_offset !== undefined) {
    offset = _offset as number;
  } else if (page['page_no'] !== undefined) {
    offset = (page['page_no'] as number) * pageSize;
  }
  const calculatedPageNo = Math.floor(offset / pageSize);
  return {
    ...rest,
    page: { ...page, page_no: calculatedPageNo },
  };
}

function transformFuelPostBody(body: Record<string, unknown>, url: string): Record<string, unknown> {
  if (!url.includes('/filter')) {
    return body;
  }

  let working = { ...body };

  if (isJretailFilterBody(working)) {
    working = transformFuelFilterRequest(working);
  } else if (Array.isArray(working['search_requests'])) {
    working = transformFuelFilterRequestBody(working as FuelFilterRequestBody);
  }

  const page = working['page'] as Record<string, unknown> | undefined;
  if (page && 'page_no' in page) {
    working = syncFuelPageNo(working);
  }

  return working;
}

function transformError(error: HttpErrorResponse): HttpErrorResponse {
  const errorBody = (error.error ?? {}) as Record<string, unknown>;
  const transformed: Record<string, unknown> = { ...errorBody };

  if (errorBody['uimessage'] && Array.isArray(errorBody['uimessage'])) {
    transformed['uimessage'] = (errorBody['uimessage'] as unknown[]).map((msg) => {
      if (typeof msg === 'string') return { text: msg };
      const m = msg as Record<string, unknown>;
      return m['text'] ? m : { text: JSON.stringify(msg) };
    });
  } else {
    const errorMessage = (errorBody['message'] as string) || (errorBody['error'] as string) || 'An error occurred';
    transformed['uimessage'] = [{ text: errorMessage }];
  }

  if (transformed['iomessage'] && Array.isArray(transformed['iomessage'])) {
    transformed['iomessage'] = (transformed['iomessage'] as unknown[]).map((msg) => {
      if (typeof msg === 'string') return { text: msg };
      const m = msg as Record<string, unknown>;
      return m['text'] ? m : { text: JSON.stringify(msg) };
    });
  } else {
    transformed['iomessage'] = transformed['uimessage'];
  }

  return new HttpErrorResponse({
    error: transformed,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  });
}

export const fuelManagementInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('fuel-management-service')) {
    return next(req);
  }

  const translate = inject(TranslateService);
  const langService = inject(AppTranslateLanguageService);
  const http = inject(HttpClient);

  let modifiedReq = req.clone({
    setHeaders: { 'Accept-Language': getAcceptLanguage(translate, langService) },
  });

  if (req.method === 'POST') {
    const body = req.body as Record<string, unknown> | null;
    if (body && typeof body === 'object') {
      const transformed = transformFuelPostBody(body, req.url);
      if (req.url.includes('/filter') || transformed !== body) {
        modifiedReq = modifiedReq.clone({ body: transformed });
      }
    }
  }

  return next(modifiedReq).pipe(
    switchMap((event) => {
      if (!(event instanceof HttpResponse)) {
        return of(event);
      }
      if (!event.url?.includes('fuel-management-service')) {
        return of(event);
      }

      const body = (event.body ?? {}) as FuelResponseBody;
      const companyId = getCompanyId();

      const branches$: Observable<BranchRow[]> = companyId != null ? fetchBranches(http, companyId) : of([]);

      return forkJoin([branches$]).pipe(map(([branches]) => event.clone({ body: transformResponse(body, branches) })));
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.url?.includes('fuel-management-service')) {
        return throwError(() => transformError(error));
      }
      return throwError(() => error);
    })
  );
};
